import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Modal } from "@/components/modal";
import { Button } from "@/components/ui/button";

/**
 * A username box that may be left empty is never sent so without a word
 * (ADR 0076): the form's submit is stopped, and a sheet says a handle will
 * be chosen — Choose one goes back to the box, the other button sends the
 * form as it is. Accepted once, it is not asked again on that page.
 *
 * `onSubmit` goes on the page's `<Form>`; `sheet` is drawn beside it.
 */
export function useUsernameWarning({
  name = "username",
  says,
  accept,
}: {
  /** The box's `name` in the form. */
  name?: string;
  /** What will happen without one. */
  says: ReactNode;
  /** The button that lets it happen. */
  accept: string;
}) {
  const [asking, setAsking] = useState<HTMLFormElement | null>(null);
  const accepted = useRef(false);
  const focusNext = useRef<HTMLElement | null>(null);

  // After the sheet has gone, and after it has handed the focus back to
  // where it came from — the submit button — so the box's lands last.
  useEffect(() => {
    const element = focusNext.current;
    if (asking || !element) return;
    focusNext.current = null;
    const timer = setTimeout(() => element.focus(), 0);
    return () => clearTimeout(timer);
  }, [asking]);

  const box = (form: HTMLFormElement) => {
    const element = form.elements.namedItem(name);
    return element instanceof HTMLInputElement ? element : null;
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    const input = box(event.currentTarget);
    if (accepted.current || !input || input.value.trim() !== "") return;
    event.preventDefault();
    setAsking(event.currentTarget);
  };

  const sheet = asking && (
    <Modal title="No username chosen" onClose={() => setAsking(null)}>
      <div className="text-sm text-muted-foreground">{says}</div>
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => {
            focusNext.current = box(asking);
            setAsking(null);
          }}
        >
          Choose one
        </Button>
        <Button
          type="button"
          size="lg"
          onClick={() => {
            accepted.current = true;
            setAsking(null);
            asking.requestSubmit();
          }}
        >
          {accept}
        </Button>
      </div>
    </Modal>
  );

  return { onSubmit, sheet };
}
